import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings as SettingsIcon, X, Shield, Globe2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  CATEGORY_ESSENTIAL,
  CATEGORY_EXTERNAL,
  useCookieConsent,
} from "@/context/CookieConsentContext";

function Toggle({ checked, disabled, onChange, label, hint, icon: Icon, dataTestid }) {
  return (
    <div className="flex items-start gap-4 rounded-xl border border-[#EAEBE6] bg-[#FCFCFB] p-4">
      <div className="h-9 w-9 shrink-0 rounded-lg bg-[#A7B584]/15 grid place-items-center text-[#8C9A69]">
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-semibold text-[#5A5B5C]">{label}</div>
          <button
            type="button"
            onClick={() => !disabled && onChange(!checked)}
            disabled={disabled}
            data-testid={dataTestid}
            aria-pressed={checked}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
              checked ? "bg-[#A7B584]" : "bg-[#D9DBD2]"
            } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                checked ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
        <p className="mt-1.5 text-xs text-[#272525] leading-relaxed">{hint}</p>
      </div>
    </div>
  );
}

export default function CookieBanner() {
  const { consent, open, acceptAll, acceptEssentialOnly, saveSelection, reopen, hydrated } =
    useCookieConsent();
  const [showSettings, setShowSettings] = useState(false);
  const [externalAllowed, setExternalAllowed] = useState(consent[CATEGORY_EXTERNAL]);

  // sync local toggle state when consent updates externally
  React.useEffect(() => {
    setExternalAllowed(consent[CATEGORY_EXTERNAL]);
  }, [consent]);

  if (!hydrated) return null;

  return (
    <>
      {/* Floating widget (bottom-left, like Borlabs) — only when banner is closed */}
      {!open && (
        <button
          type="button"
          onClick={() => {
            setShowSettings(true);
            reopen();
          }}
          aria-label="Datenschutz-Einstellungen öffnen"
          data-testid="cookie-widget-button"
          className="fixed bottom-5 left-5 z-40 h-11 w-11 rounded-full bg-white shadow-lg border border-[#EAEBE6] grid place-items-center text-[#8C9A69] hover:bg-[#F4F5F2] transition-colors"
        >
          <Cookie className="h-5 w-5" />
        </button>
      )}

      {/* Banner / Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pointer-events-none"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-banner-title"
          data-testid="cookie-banner"
        >
          {/* subtle backdrop */}
          <div className="absolute inset-0 bg-[#1E1F1A]/30 backdrop-blur-[2px] pointer-events-auto" />

          <div className="relative w-full sm:max-w-2xl m-0 sm:m-6 bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-[#EAEBE6] pointer-events-auto overflow-hidden">
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-[#A7B584]/15 grid place-items-center text-[#8C9A69]">
                    <Cookie className="h-5 w-5" />
                  </div>
                  <h2
                    id="cookie-banner-title"
                    className="text-xl sm:text-2xl font-semibold text-[#5A5B5C] tracking-tight"
                  >
                    Datenschutz-Einstellungen
                  </h2>
                </div>
                {consent.decided && (
                  <button
                    type="button"
                    aria-label="Schließen"
                    onClick={() => saveSelection({ [CATEGORY_EXTERNAL]: externalAllowed })}
                    className="p-2 rounded-lg text-[#5A5B5C] hover:bg-[#F4F5F2]"
                    data-testid="cookie-close-btn"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <p className="mt-4 text-sm text-[#272525] leading-relaxed">
                Wir verwenden Cookies und vergleichbare Technologien auf unserer Website. Einige
                sind essenziell, andere helfen uns, externe Medien wie Schriftarten und Bilder
                bereitzustellen. Sie können Ihre Auswahl jederzeit unter „Einstellungen" anpassen
                — Details finden Sie in unserer{" "}
                <Link
                  to="/datenschutz"
                  className="text-[#A7B584] hover:text-[#8C9A69] underline underline-offset-4"
                >
                  Datenschutzerklärung
                </Link>{" "}
                und im{" "}
                <Link
                  to="/impressum"
                  className="text-[#A7B584] hover:text-[#8C9A69] underline underline-offset-4"
                >
                  Impressum
                </Link>
                .
              </p>

              {showSettings && (
                <div className="mt-6 space-y-3" data-testid="cookie-settings-panel">
                  <Toggle
                    checked={true}
                    disabled
                    label="Essenziell"
                    icon={Shield}
                    onChange={() => {}}
                    dataTestid="cookie-toggle-essential"
                    hint="Technisch notwendige Cookies für Anmeldung und Sitzungsverwaltung (access_token, refresh_token, session_token). Ohne diese funktioniert die Webseite nicht."
                  />
                  <Toggle
                    checked={externalAllowed}
                    onChange={setExternalAllowed}
                    label="Externe Medien"
                    icon={Globe2}
                    dataTestid="cookie-toggle-external"
                    hint="Lädt externe Schriftarten von Google Fonts (DM Sans) sowie Bilder von Unsplash und Pexels. Hierbei kann Ihre IP-Adresse an die jeweiligen Anbieter übertragen werden."
                  />
                </div>
              )}

              <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setShowSettings((v) => !v)}
                  data-testid="cookie-toggle-settings"
                  className="inline-flex items-center justify-center gap-2 text-sm text-[#5A5B5C] hover:text-[#272525] font-medium px-3 py-2"
                >
                  <SettingsIcon className="h-4 w-4" />
                  {showSettings ? "Einstellungen ausblenden" : "Einstellungen"}
                </button>

                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={acceptEssentialOnly}
                    data-testid="cookie-essential-btn"
                    className="bg-white border-[#EAEBE6] hover:bg-[#F4F5F2] text-[#272525] rounded-lg font-semibold"
                  >
                    Nur essenzielle
                  </Button>
                  {showSettings ? (
                    <Button
                      type="button"
                      onClick={() => saveSelection({ [CATEGORY_EXTERNAL]: externalAllowed })}
                      data-testid="cookie-save-btn"
                      className="bg-[#1E1F1A] hover:bg-[#272525] text-white rounded-lg font-semibold"
                    >
                      Auswahl speichern
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    onClick={acceptAll}
                    data-testid="cookie-accept-all-btn"
                    className="bg-[#A7B584] hover:bg-[#8C9A69] text-white rounded-lg font-semibold"
                  >
                    Alle akzeptieren
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
